interface ValidationErrors {
  nonWorkingDays?: boolean
  normalWeekWorkingDays?: boolean
  absenceReasonText?: boolean
  medicalPractitionerComments?: boolean
}

interface EditableFields {
  nonWorkingDays?: string | number
  normalWeekWorkingDays?: string | number
  absenceReasonText?: string
  medicalPractitionerConsultedType: string
  medicalPractitionerComments?: string
}

/**
 * Validates if working days value is within acceptable range
 * @param days - The working days value to validate
 * @returns true if invalid, false if valid
 */
export const isInvalidWorkingDays = (days: string | number | undefined): boolean => (
  days === undefined ||
  Number(days) < 1 ||
  Number(days) > 7 ||
  !days
)

/**
 * Checks if medical practitioner comments are required
 * @param fields - The editable fields object
 * @returns true if comments are required but missing
 */
export const isMedicalCommentsRequired = (fields: EditableFields): boolean => (
  !fields.medicalPractitionerComments &&
  fields.medicalPractitionerConsultedType.toLowerCase() === 'yes'
)

/**
 * Validates all form fields for SCA submission
 * @param fields - The editable fields to validate
 * @returns object containing validation errors
 */
export const validateScaFields = (fields: EditableFields | undefined): ValidationErrors => {
  if (!fields) {
    return {
      nonWorkingDays: true,
      normalWeekWorkingDays: true,
      absenceReasonText: true,
    }
  }

  const errors: ValidationErrors = {}

  if (fields.nonWorkingDays === undefined) {
    errors.nonWorkingDays = true
  }

  if (isInvalidWorkingDays(fields.normalWeekWorkingDays)) {
    errors.normalWeekWorkingDays = true
  }

  if (!fields.absenceReasonText) {
    errors.absenceReasonText = true
  }

  if (isMedicalCommentsRequired(fields)) {
    errors.medicalPractitionerComments = true
  }

  return errors
}

/**
 * Checks if any validation errors exist
 * @param errors - The validation errors object
 * @returns true if there are validation errors
 */
export const hasValidationErrors = (errors: ValidationErrors): boolean =>
  Object.values(errors).some(error => error === true)

/**
 * Sets validation error states based on validation results
 * @param errors - The validation errors object
 * @param setters - Object containing error setter functions
 */
export const setValidationErrors = (
  errors: ValidationErrors,
  setters: {
    setNonWorkingDaysError: (value: boolean) => void
    setNormalWeekWorkingDaysError: (value: boolean) => void
    setAbsenceReasonTextError: (value: boolean) => void
    setMedicalPractitionerCommentsError: (value: boolean) => void
  }
) => {
  if (errors.nonWorkingDays) {
    setters.setNonWorkingDaysError(true)
  }
  if (errors.normalWeekWorkingDays) {
    setters.setNormalWeekWorkingDaysError(true)
  }
  if (errors.absenceReasonText) {
    setters.setAbsenceReasonTextError(true)
  }
  if (errors.medicalPractitionerComments) {
    setters.setMedicalPractitionerCommentsError(true)
  }
}